/**
 * ED author entry — an explicit author action, never a MASTER-card click.
 * Ten QA showcase samples remain read-only. A confirmed action creates either:
 * - a named ordinary draft + one blank typed spread, or
 * - one blank typed spread inside a chosen existing draft in the same volume.
 * No sample text, images, IDs or settings are copied.
 */
export const EDITORIAL_MASTER_SHOWCASE_ID = "0f26cb7d-f6ce-484c-b9a0-596b33e60419";
const ED = "/admin/editorial";
const AUTHOR_MASTER_TYPES = new Set([
  "HERO", "EDITORIAL_STORY", "VISUAL_STORY", "MACRO", "MACRO_SENSORY",
  "PORTRAIT_DIALOGUE", "RECIPE", "HISTORICAL_EDITORIAL", "KNOWLEDGE", "VIDEO",
]);

function verifySource(sourceMaterial, sourceBlock) {
  if (sourceMaterial?.id !== EDITORIAL_MASTER_SHOWCASE_ID) {
    throw new Error("Авторский шаблон не найден.");
  }
  if (!sourceMaterial.volume_id || !AUTHOR_MASTER_TYPES.has(sourceBlock?.master_type) || !sourceBlock.id
      || (sourceBlock.material_id && sourceBlock.material_id !== sourceMaterial.id)) {
    throw new Error("Не удалось определить выбранный MASTER.");
  }
}

async function createBlankSpread(api, sourceBlock, targetMaterial) {
  const { data: block } = await api.post(
    `${ED}/materials/${encodeURIComponent(targetMaterial.id)}/blocks`,
    {
      material_id: targetMaterial.id,
      master_type: sourceBlock.master_type,
      sort_order: 0,
      is_visible: true,
      content: {},
      settings: {},
    },
  );
  if (!block?.id || block.material_id !== targetMaterial.id || block.id === sourceBlock.id
      || block.master_type !== sourceBlock.master_type) {
    throw new Error("Не удалось создать независимый редакционный разворот.");
  }
  return block;
}

export async function beginAuthorMaterialFromMaster({ api, sourceMaterial, sourceBlock, title }) {
  verifySource(sourceMaterial, sourceBlock);
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) throw new Error("Введите название нового материала.");
  const { data: material } = await api.post(`${ED}/materials`, {
    volume_id: sourceMaterial.volume_id,
    title: cleanTitle,
    status: "draft",
  });
  if (typeof material?.id !== "string" || !material.id.trim() || material.id === sourceMaterial.id
      || material.volume_id !== sourceMaterial.volume_id || material.status !== "draft") {
    throw new Error("Не удалось создать отдельный авторский материал.");
  }

  try {
    const block = await createBlankSpread(api, sourceBlock, material);
    return { material, block };
  } catch (error) {
    // Delete only this operation's freshly created draft, never the QA sample
    // or an existing article. An invalid creation response is not trusted.
    try {
      await api.delete(`${ED}/materials/${encodeURIComponent(material.id)}`);
    } catch {
      throw new Error("Новый черновик создан не полностью. Проверьте список материалов.", { cause: error });
    }
    throw error;
  }
}

export async function addAuthorSpreadToExistingMaterial({ api, sourceMaterial, sourceBlock, targetMaterial }) {
  verifySource(sourceMaterial, sourceBlock);
  if (!targetMaterial?.id || targetMaterial.id === sourceMaterial.id
      || targetMaterial.volume_id !== sourceMaterial.volume_id || targetMaterial.status !== "draft") {
    throw new Error("Выберите существующий авторский черновик этого тома.");
  }
  const block = await createBlankSpread(api, sourceBlock, targetMaterial);
  return { material: targetMaterial, block };
}


/**
 * ED-V1: persist the FIRST authored Story on explicit Save only.
 * The caller supplies clean, user-entered content from its blank local
 * workspace; no QA example text/photo/settings may be passed through.
 * Source MASTER records are NEVER patched or deleted.
 */
export async function saveFirstEditorialStoryFromMaster({
  api, sourceMaterial, sourceBlock, title, content, settings,
}) {
  verifySource(sourceMaterial, sourceBlock);
  if (sourceBlock.master_type !== "EDITORIAL_STORY") {
    throw new Error("Этот этап поддерживает только редакционную историю.");
  }
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) throw new Error("Укажите заголовок авторской статьи.");
  if (!content || typeof content !== "object" || !String(content.body || "").trim()) {
    throw new Error("Добавьте собственный текст статьи перед сохранением.");
  }
  if (!settings || typeof settings !== "object") {
    throw new Error("Не удалось подготовить оформление статьи.");
  }
  if (JSON.stringify(content) === JSON.stringify(sourceBlock.content || {})) {
    throw new Error("Служебный образец нельзя сохранить как авторскую статью без изменений.");
  }

  const created = await beginAuthorMaterialFromMaster({
    api, sourceMaterial, sourceBlock, title: cleanTitle,
  });
  try {
    await api.patch(`${ED}/blocks/${encodeURIComponent(created.block.id)}`, {
      content, settings,
    });
    return created;
  } catch (error) {
    // Only delete this action's new draft, never the source fixture.
    // If cleanup fails, surface a human-readable blocking error instead
    // of attempting a second create and silently duplicating a draft.
    try {
      await api.delete(`${ED}/materials/${encodeURIComponent(created.material.id)}`);
    } catch {
      throw new Error("Черновик создан не полностью. Проверьте список материалов перед повтором.", { cause: error });
    }
    throw error;
  }
}
